import {
  ref,
  onValue,
  set,
  push,
  onDisconnect,
  serverTimestamp,
  get,
  update,
  DatabaseReference,
  DataSnapshot,
  off
} from 'firebase/database';
import { rtdb } from './firebase/config';
import { AuthService } from './AuthService';
import { HexCoord } from '../game/hex/HexTypes';

export interface PlayerInfo {
  uid: string;
  displayName: string;
  isAnonymous: boolean;
  joinedAt: number;
  status: 'waiting' | 'ready' | 'playing' | 'finished';
  score: number;
  completionTime?: number;
}

export interface BoardData {
  seed: string;
  tileSequence: string[]; // 全タイルの配置順序
  roundPlacements: { [round: number]: { coord: string, type: string }[] }; // 各ラウンドのタイル配置位置と種類
}

export interface GameRoom {
  id: string;
  hostId: string;
  status: 'waiting' | 'starting' | 'playing' | 'finished';
  maxPlayers: number;
  players: { [uid: string]: PlayerInfo };
  boardData?: BoardData; // ホストが生成する盤面データ
  currentRound?: number;
  startTime?: number;
  endTime?: number;
  createdAt: number;
}

export interface GameMove {
  playerId: string;
  marbleIndex: number;
  targetCoord: HexCoord;
  timestamp: number;
  moveNumber: number;
}

export interface RoundProgress {
  playerId: string;
  roundNumber: number;
  completedAt: number;
}

export interface RoomListEntry {
  id: string;
  hostName: string;
  playerCount: number;
  maxPlayers: number;
  status: string;
  createdAt: number;
}

export class MultiplayerService {
  private authService: AuthService;
  private currentRoomRef: DatabaseReference | null = null;
  private roomListeners: Map<string, (snapshot: DataSnapshot) => void> = new Map();
  
  constructor(authService: AuthService) {
    this.authService = authService;
  }

  /**
   * ルーム一覧を取得
   */
  async getRoomList(): Promise<RoomListEntry[]> {
    try {
      const roomsRef = ref(rtdb, 'rooms');
      const snapshot = await get(roomsRef);
      
      if (!snapshot.exists()) {
        console.log('No rooms found');
        return [];
      }
      
      const rooms: RoomListEntry[] = [];
      const now = Date.now();
      const oneHourAgo = now - (60 * 60 * 1000);
    
    snapshot.forEach((roomSnapshot) => {
      const room = roomSnapshot.val() as GameRoom;
      
      // データが不完全な場合はスキップ
      if (!room || !room.players) {
        console.warn('Invalid room data:', roomSnapshot.key);
        return;
      }
      
      // 1時間以上前の終了したゲームは表示しない
      if (room.status === 'finished' && room.endTime && room.endTime < oneHourAgo) {
        return;
      }
      
      // 進行中または待機中のゲームのみ
      if (room.status === 'waiting' || room.status === 'starting') {
        rooms.push({
          id: roomSnapshot.key!,
          hostName: room.players[room.hostId]?.displayName || 'Unknown',
          playerCount: Object.keys(room.players).length,
          maxPlayers: room.maxPlayers || 4,
          status: room.status,
          createdAt: room.createdAt || Date.now()
        });
      }
    });
    
    // 新しい順にソート
    return rooms.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      console.error('Failed to get room list:', error);
      return [];
    }
  }

  /**
   * 新しいゲームルームを作成
   */
  async createRoom(maxPlayers: number = 4): Promise<string> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      throw new Error('ログインが必要です');
    }
    
    const roomRef = push(ref(rtdb, 'rooms'));
    const roomId = roomRef.key!;
    
    const playerInfo: PlayerInfo = {
      uid: user.uid,
      displayName: user.displayName,
      isAnonymous: user.isAnonymous,
      joinedAt: Date.now(),
      status: 'waiting',
      score: 0
    };
    
    const roomData: GameRoom = {
      id: roomId,
      hostId: user.uid,
      status: 'waiting',
      maxPlayers,
      players: {
        [user.uid]: playerInfo
      },
      createdAt: Date.now()
    };
    
    await set(roomRef, roomData);
    
    // 切断時の処理を設定
    this.setupDisconnectHandlers(roomId, user.uid);
    
    this.currentRoomRef = roomRef;
    return roomId;
  }

  /**
   * 既存のルームに参加
   */
  async joinRoom(roomId: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      throw new Error('ログインが必要です');
    }
    
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const roomSnapshot = await get(roomRef);
    
    if (!roomSnapshot.exists()) {
      throw new Error('ルームが存在しません');
    }
    
    const room = roomSnapshot.val() as GameRoom;
    
    // 参加可能かチェック
    if (room.status !== 'waiting') {
      throw new Error('このルームは既にゲームが開始されています');
    }
    
    const playerCount = Object.keys(room.players).length;
    if (playerCount >= room.maxPlayers) {
      throw new Error('ルームが満員です');
    }
    
    // プレイヤー情報を追加
    const playerInfo: PlayerInfo = {
      uid: user.uid,
      displayName: user.displayName,
      isAnonymous: user.isAnonymous,
      joinedAt: Date.now(),
      status: 'waiting',
      score: 0
    };
    
    await update(ref(rtdb, `rooms/${roomId}/players`), {
      [user.uid]: playerInfo
    });
    
    // 切断時の処理を設定
    this.setupDisconnectHandlers(roomId, user.uid);
    
    this.currentRoomRef = roomRef;
  }

  /**
   * ルームから退出
   */
  async leaveRoom(roomId: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const roomSnapshot = await get(roomRef);
    
    if (!roomSnapshot.exists()) return;
    
    const room = roomSnapshot.val() as GameRoom;
    
    // 自分を削除
    await set(ref(rtdb, `rooms/${roomId}/players/${user.uid}`), null);
    
    // ホストが退出した場合
    if (room.hostId === user.uid) {
      const remainingPlayers = Object.keys(room.players).filter(uid => uid !== user.uid);
      
      if (remainingPlayers.length > 0) {
        // 新しいホストを指定
        await update(roomRef, {
          hostId: remainingPlayers[0]
        });
      } else {
        // 誰もいなくなったらルームを削除
        await set(roomRef, null);
      }
    }
    
    // リスナーを解除
    this.removeRoomListeners(roomId);
    this.currentRoomRef = null;
  }

  /**
   * プレイヤーの準備状態を更新
   */
  async setPlayerReady(roomId: string, ready: boolean): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    await update(ref(rtdb, `rooms/${roomId}/players/${user.uid}`), {
      status: ready ? 'ready' : 'waiting'
    });
  }

  /**
   * ボードデータを生成（ホスト用）
   */
  generateBoardData(): BoardData {
    // シードを生成
    const seed = Math.random().toString(36).substring(2, 15) + 
                 Math.random().toString(36).substring(2, 15);
    
    // 擬似乱数生成器
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash = hash & hash;
    }
    const seededRandom = () => {
      hash = (hash * 1103515245 + 12345) & 0x7fffffff;
      return hash / 0x7fffffff;
    };
    
    // タイル配置を生成（19個 = 中央1 + Ring1:6 + Ring2:12）
    // ミニゲームでは6ラウンドかけて19個全部を配置する
    const config = {
      BLUE_1: 3,
      RED_1: 3,
      BLUE_2: 3,
      RED_2: 2,
      BLUE_1_RED_1: 4,
      BLUE_2_RED_1: 3,
      BLUE_1_RED_2: 1
    };
    
    const tileSequence: string[] = [];
    Object.entries(config).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        tileSequence.push(type);
      }
    });
    
    // シャッフル
    for (let i = tileSequence.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [tileSequence[i], tileSequence[j]] = [tileSequence[j], tileSequence[i]];
    }
    
    // 6角の座標
    const cornerCoords = [
      '0,2,-2', '2,0,-2', '2,-2,0',
      '0,-2,2', '-2,0,2', '-2,2,0'
    ];
    
    // 全19タイルの座標リスト
    const allCoords = [
      '0,0,0',  // 中央
      // Ring 1
      '0,1,-1', '1,0,-1', '1,-1,0', '0,-1,1', '-1,0,1', '-1,1,0',
      // Ring 2
      '0,2,-2', '1,1,-2', '2,0,-2', '2,-1,-1', '2,-2,0',
      '1,-2,1', '0,-2,2', '-1,-1,2', '-2,0,2', '-2,1,1',
      '-2,2,0', '-1,2,-1'
    ];
    
    // ラウンドごとのタイル配置を決定
    const roundPlacements: { [round: number]: { coord: string, type: string }[] } = {};
    let tileIndex = 0;
    
    // 全座標をシャッフル（ただし中央は最後にする傾向）
    const shuffledCoords = [...allCoords];
    // 中央を除いてシャッフル
    const centerIndex = shuffledCoords.indexOf('0,0,0');
    shuffledCoords.splice(centerIndex, 1);
    
    for (let i = shuffledCoords.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [shuffledCoords[i], shuffledCoords[j]] = [shuffledCoords[j], shuffledCoords[i]];
    }
    
    // 中央を最後の方に配置（ゲーム後半で配置される）
    const insertPosition = Math.floor(12 + seededRandom() * 6); // 12-17の位置に挿入
    shuffledCoords.splice(insertPosition, 0, '0,0,0');
    
    // 各ラウンドに配置を割り当て
    // Round 1-3: 各4個
    // Round 4-5: 各3個
    // Round 6: 1個
    const tilesPerRound = [4, 4, 4, 3, 3, 1];
    const usedCoords = new Set<string>();
    
    for (let round = 1; round <= 6; round++) {
      roundPlacements[round] = [];
      const tilesToPlace = tilesPerRound[round - 1];
      
      for (let i = 0; i < tilesToPlace && tileIndex < tileSequence.length && tileIndex < shuffledCoords.length; i++) {
        const coord = shuffledCoords[tileIndex];
        if (!usedCoords.has(coord)) {
          roundPlacements[round].push({
            coord: coord,
            type: tileSequence[tileIndex]
          });
          usedCoords.add(coord);
          tileIndex++;
        }
      }
      
      console.log(`[MultiplayerService] Round ${round}: Placed ${roundPlacements[round].length} tiles at ${roundPlacements[round].map(p => p.coord).join(', ')}`);
    }
    
    // デバッグログ
    console.log('[MultiplayerService] Board generation complete:', {
      totalTiles: tileIndex,
      roundsGenerated: Object.keys(roundPlacements).length,
      tilesPerRound: Object.entries(roundPlacements).map(([r, tiles]) => `R${r}: ${tiles.length}`).join(', ')
    });
    
    return {
      seed,
      tileSequence,
      roundPlacements
    };
  }
  
  /**
   * ゲーム開始（ホストのみ）
   */
  async startGame(roomId: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    const roomSnapshot = await get(roomRef);
    
    if (!roomSnapshot.exists()) {
      throw new Error('ルームが存在しません');
    }
    
    const room = roomSnapshot.val() as GameRoom;
    
    if (room.hostId !== user.uid) {
      throw new Error('ホストのみがゲームを開始できます');
    }
    
    // 全員準備完了チェック
    const allReady = Object.values(room.players).every(
      player => player.status === 'ready' || player.uid === room.hostId
    );
    
    if (!allReady) {
      throw new Error('全員の準備が完了していません');
    }
    
    // ボードデータを生成
    const boardData = this.generateBoardData();
    console.log('[MultiplayerService] Generated board data:', {
      seed: boardData.seed,
      rounds: Object.keys(boardData.roundPlacements).length,
      totalTiles: boardData.tileSequence.length
    });
    
    // ゲーム開始
    const updates: any = {
      status: 'playing',
      boardData,
      currentRound: 1,
      startTime: Date.now()
    };
    
    // 全プレイヤーのステータスを更新
    Object.keys(room.players).forEach(uid => {
      updates[`players/${uid}/status`] = 'playing';
    });
    
    await update(roomRef, updates);
  }

  /**
   * 移動を送信
   */
  async sendMove(roomId: string, move: Omit<GameMove, 'playerId' | 'timestamp'>): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    const moveData: GameMove = {
      ...move,
      playerId: user.uid,
      timestamp: Date.now()
    };
    
    await push(ref(rtdb, `rooms/${roomId}/moves`), moveData);
  }
  
  /**
   * ラウンド完了を送信し、全員が完了したら次のラウンドへ
   */
  async sendRoundComplete(roomId: string, roundNumber: number): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    // ラウンド完了を記録
    await update(ref(rtdb, `rooms/${roomId}/players/${user.uid}`), {
      completedRound: roundNumber,
      lastRoundCompleteAt: Date.now()
    });
    
    // 全プレイヤーの状態を確認
    const roomSnapshot = await get(ref(rtdb, `rooms/${roomId}`));
    if (!roomSnapshot.exists()) return;
    
    const room = roomSnapshot.val() as GameRoom;
    const players = Object.values(room.players);
    
    // 全員が同じラウンドを完了したかチェック
    const allCompleted = players.every((player: any) => 
      player.completedRound >= roundNumber
    );
    
    if (allCompleted && room.currentRound === roundNumber) {
      // 次のラウンドに進む
      await update(ref(rtdb, `rooms/${roomId}`), {
        currentRound: roundNumber + 1
      });
    }
  }

  /**
   * プレイヤーのスコアを更新
   */
  async updatePlayerScore(roomId: string, score: number): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    await update(ref(rtdb, `rooms/${roomId}/players/${user.uid}`), {
      score
    });
  }

  /**
   * ゲーム完了を報告
   */
  async reportCompletion(roomId: string, completionTime: number): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) return;
    
    await update(ref(rtdb, `rooms/${roomId}/players/${user.uid}`), {
      status: 'finished',
      completionTime
    });
    
    // 全員完了したかチェック
    const roomSnapshot = await get(ref(rtdb, `rooms/${roomId}`));
    if (roomSnapshot.exists()) {
      const room = roomSnapshot.val() as GameRoom;
      const allFinished = Object.values(room.players).every(
        player => player.status === 'finished'
      );
      
      if (allFinished) {
        await update(ref(rtdb, `rooms/${roomId}`), {
          status: 'finished',
          endTime: Date.now()
        });
      }
    }
  }

  /**
   * ルーム変更をリッスン
   */
  listenToRoom(roomId: string, callback: (room: GameRoom | null) => void): () => void {
    const roomRef = ref(rtdb, `rooms/${roomId}`);
    
    const listener = (snapshot: DataSnapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val() as GameRoom);
      } else {
        callback(null);
      }
    };
    
    this.roomListeners.set(`room_${roomId}`, listener);
    onValue(roomRef, listener);
    
    // クリーンアップ関数を返す
    return () => {
      off(roomRef, 'value', listener);
      this.roomListeners.delete(`room_${roomId}`);
    };
  }

  /**
   * 移動をリッスン
   */
  listenToMoves(roomId: string, callback: (move: GameMove) => void): () => void {
    const movesRef = ref(rtdb, `rooms/${roomId}/moves`);
    
    const listener = (snapshot: DataSnapshot) => {
      if (snapshot.exists()) {
        const move = snapshot.val() as GameMove;
        callback(move);
      }
    };
    
    this.roomListeners.set(`moves_${roomId}`, listener);
    onValue(movesRef, listener, { onlyOnce: false });
    
    // クリーンアップ関数を返す
    return () => {
      off(movesRef, 'child_added', listener);
      this.roomListeners.delete(`moves_${roomId}`);
    };
  }
  

  /**
   * 切断時の処理を設定
   */
  private setupDisconnectHandlers(roomId: string, userId: string): void {
    const playerRef = ref(rtdb, `rooms/${roomId}/players/${userId}`);
    
    // 切断時にプレイヤーを削除
    onDisconnect(playerRef).remove();
    
    // プレゼンス管理
    const presenceRef = ref(rtdb, `presence/${userId}`);
    set(presenceRef, {
      online: true,
      lastSeen: serverTimestamp()
    });
    
    onDisconnect(presenceRef).set({
      online: false,
      lastSeen: serverTimestamp()
    });
  }

  /**
   * リスナーを解除
   */
  private removeRoomListeners(roomId: string): void {
    this.roomListeners.forEach((listener, key) => {
      if (key.startsWith(`room_${roomId}`) || key.startsWith(`moves_${roomId}`)) {
        // リスナーの解除はlistenTo関数で返されるクリーンアップ関数で行う
        this.roomListeners.delete(key);
      }
    });
  }

  /**
   * 現在のルームIDを取得
   */
  getCurrentRoomId(): string | null {
    return this.currentRoomRef?.key || null;
  }
}